import { Stack, Typography } from '@mui/joy';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { useUserPermissions } from 'auth/hooks';
import { ICliJob } from 'cli/types';
import { PreviewCsv } from 'core/components';
import { Loader } from 'core/elements';
import { AccessPermissions, ApiStatus, NotificationPosition } from 'core/enums';
import { useCliJobHandler } from 'core/hooks';
import { useNotificationContext } from 'core/states';
import { getLocalDateForServer } from 'core/utils';
import {
  isSkuExportPreviewResult,
  mapAttributeFormToSkuExportRequest,
} from 'quotes/components/ExportSkuPart/mappers';
import { IAttributeForm } from 'quotes/components/ExportSkuPart/types';
import { VendorQuoteHelper } from 'quotes/helpers';
import { useQuoteActions } from 'quotes/hooks';
import { IVendorQuote } from 'quotes/types';
import { Color, Level } from 'theme/enums';

interface IProps {
  quote: IVendorQuote;
  formData: IAttributeForm | null;
  onPreviewReady: (isPreviewReady: boolean) => void;
}

export const Preview = ({ quote, formData, onPreviewReady }: IProps) => {
  const { t } = useTranslation();

  const [content, setContent] = useState<string | null>(null);
  const [hasErrored, setHasErrored] = useState(false);

  const hasFiredCliJobRef = useRef(false);

  const spaStatusLabelKey = VendorQuoteHelper.getSpaStatusLabelKey(formData?.spaStatus);

  const { clearNotification, showNotification } = useNotificationContext();
  const { handleCliJob } = useCliJobHandler();
  const { exportQuote } = useQuoteActions();
  const queryClient = useQueryClient();
  const { hasPermission } = useUserPermissions();
  // The flag follows the Attributes gate too: the drawer is read-only for a blocked user and the
  // BE skips the persist, so only send it when this user may edit the Attributes.
  const canEnableSelectableSpa =
    hasPermission(AccessPermissions.SELECTABLE_SPA) &&
    !VendorQuoteHelper.isAttributesEditBlocked(quote);
  const canChooseSpaStatus = hasPermission(AccessPermissions.ALLOW_LIVE_OR_PLANNING_SPA);

  useEffect(() => {
    if (!formData) {
      return;
    }

    if (hasFiredCliJobRef.current) {
      return;
    }

    hasFiredCliJobRef.current = true;

    const onCliJobFinished = (cliJob: ICliJob) => {
      if (!isSkuExportPreviewResult(cliJob.result)) {
        setHasErrored(true);
        showNotification({
          type: ApiStatus.ERROR,
          title: t('titleError'),
          message: t('messageExportSkuPartsFileMissing'),
          position: NotificationPosition.DRAWER,
        });

        return;
      }

      // The BE still saves the submitted options as the quote's Attributes on a preview.
      // Partial key match: the exact details key carries a `{ quoteId, isOrder? }` criteria object.
      queryClient.invalidateQueries(['quote']);

      setContent(cliJob.result.content);
      onPreviewReady(true);
    };

    const onCliJobStopped = (cliJob: ICliJob) => {
      setHasErrored(true);
      showNotification({
        type: ApiStatus.ERROR,
        title: t('titleError'),
        message: cliJob.statusDescription || t('serverError'),
        position: NotificationPosition.DRAWER,
      });
    };

    handleCliJob(
      exportQuote,
      mapAttributeFormToSkuExportRequest({
        quoteId: quote.id,
        formData,
        persist: false,
        canChooseSpaStatus,
        canEnableSelectableSpa,
        localDate: getLocalDateForServer(),
      }),
      {
        cliJobInProgressMessage: t('messageGenerateExportSkuPartsInProgress'),
        notificationPosition: NotificationPosition.DRAWER,
        onCliJobFinished,
        onCliJobStopped,
        // Show the BE copy of a refused persist (e.g. the Selected SPA 403), not axios'
        // generic text.
        preferBackendError: true,
      }
    );
  }, [
    formData,
    canChooseSpaStatus,
    canEnableSelectableSpa,
    quote.id,
    handleCliJob,
    exportQuote,
    showNotification,
    t,
    onPreviewReady,
    queryClient,
  ]);

  if (hasErrored) {
    return (
      <Stack sx={{ alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 240 }}>
        <Typography level={Level.BODY_MD} color={Color.DANGER}>
          {t('messageExportSkuPartsFileMissing')}
        </Typography>
      </Stack>
    );
  }

  if (null === content) {
    return (
      <Stack sx={{ alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 240 }}>
        <Loader />
      </Stack>
    );
  }

  return (
    <Stack sx={{ gap: 1.5 }}>
      {spaStatusLabelKey && (
        <Typography level={Level.BODY_MD}>
          <Trans i18nKey="messageSendAsSpaSpaStatus" values={{ status: t(spaStatusLabelKey) }} />
        </Typography>
      )}

      <PreviewCsv
        content={content}
        inline
        // Leaves room for the SPA status line above the table.
        wrapperSx={{ maxHeight: 'calc(100vh - 324px)' }}
        onLoaded={clearNotification}
        onError={clearNotification}
      />
    </Stack>
  );
};
